<template>
  <div class="container mt-5">
    <h2 class="text-center">Sign In</h2>
    <form @submit.prevent="handleLogin" class="mt-4">
      <!-- Email Field -->
      <div class="mb-3">
        <label for="email" class="form-label">Email</label>
        <input
          type="email"
          class="form-control"
          id="email"
          v-model="email"
          required
        />
      </div>

      <!-- Password Field -->
      <div class="mb-3">
        <label for="password" class="form-label">Password</label>
        <input
          type="password"
          class="form-control"
          id="password"
          v-model="password"
          required
        />
      </div>

      <!-- Error Message -->
      <div v-if="error" class="alert" :class="errorClass">
        {{ error }}
        <div v-if="retryCountdown > 0" class="mt-2">
          You can try again in {{ retryCountdown }} seconds
        </div>
      </div>

      <!-- Buttons -->
      <button
        type="submit"
        class="btn btn-primary w-100"
        :disabled="isLoading || retryCountdown > 0"
      >
        <span v-if="isLoading" class="spinner-border spinner-border-sm" role="status"></span>
        <span v-else-if="retryCountdown > 0">Please wait...</span>
        <span v-else>Sign In</span>
      </button>
      <button
        type="button"
        class="btn btn-link w-100 mt-2"
        @click="$emit('show-forgot-password')"
      >
        Forgot Password?
      </button>
    </form>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";

export default {
  name: "SignIn",
  data() {
    return {
      email: "", // User's email
      password: "", // User's password
      isLoading: false, // Loading state for the login button
      error: null, // Error message on login failure
      retryCountdown: 0, // Countdown timer for rate limit
      countdownInterval: null, // Interval reference for countdown
    };
  },
  computed: {
    ...mapState({
      drawPoints: state => state.drawPoints,
    }),
    errorClass() {
      return {
        'alert-danger': !this.retryCountdown,
        'alert-warning': this.retryCountdown > 0
      };
    }
  },
  methods: {
    ...mapActions(['login', 'setDrawPoints']), // Map Vuex actions
    
    startRetryCountdown(seconds) {
      this.retryCountdown = seconds;
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
      }
      this.countdownInterval = setInterval(() => {
        if (this.retryCountdown > 0) {
          this.retryCountdown--;
        } else {
          clearInterval(this.countdownInterval);
        }
      }, 1000);
    },

    async handleLogin() {
      if (this.retryCountdown > 0) {
        return; // Prevent submission during countdown
      }

      this.isLoading = true;
      this.error = null;

      try {
        // API Call
        const response = await fetch(this.$API_BASE_URL + "/api/customers/login", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: 'include',
          body: JSON.stringify({
            email: this.email,
            password: this.password,
          }),
        });

        if (response.ok) {
          const responseData = await response.json();
          
          // Check if email is verified using dateEmailVerified field
          // If dateEmailVerified is null or undefined, email is not verified
          if (!responseData.dateEmailVerified) {
            // Don't log the user in, redirect to check email page
            this.$emit('show-check-email', this.email);
            return;
          }
          
          // Email is verified, proceed with normal login flow
          // Commit to Vuex store
          const expirationTimeStamp = Date.now() + (24 * 60 * 60 * 1000);
          console.log("Setting expiration timestamp:", expirationTimeStamp);
          this.login(expirationTimeStamp);
          
          // Check if login worked
          console.log("After login - isAuthenticated from store:", this.$store.getters.isAuthenticated);
          await this.fetchDrawPoints();
          console.log(this.drawPoints);
          if (!this.drawPoints || this.drawPoints.length === 0) {
            this.$emit('show-manual-points');
          } else {
            this.$emit('show-home')
          }
        } else {
          const errorData = await response.json().catch(() => null);
          
          if (response.status === 429) {
            // Handle rate limiting
            this.error = errorData?.message || "Too many login attempts. Please wait before trying again.";
            const retryAfter = response.headers.get('Retry-After');
            if (retryAfter) {
              this.startRetryCountdown(parseInt(retryAfter));
            }
          } else if (response.status === 404 || response.status === 401) {
            this.error = "Failed to sign in. Please create an account or reset password below!";
          } else {
            this.error = errorData?.message || "Sorry! We seem to be experiencing connection issues.";
          }
        }
      } catch (err) {
        // Handle network or unexpected errors
        this.error = "Sorry! We seem to be experiencing connection issues.";
        console.error(err);
      } finally {
        this.isLoading = false;
      }
    },

    async fetchDrawPoints() {
      try {
        const response = await fetch(this.$API_BASE_URL + '/api/drawpoints', {
          method: 'GET',
          credentials: "include",
          headers: {
            'Content-Type': 'application/json'
          }
        });
        if (response.ok) {
          const data = await response.json();
          await this.setDrawPoints(data.drawPointsList);
        }
      } catch (error) {
        console.error('Error:', error);
      }
    },
  },
  beforeDestroy() {
    // Clean up interval when component is destroyed
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
  }
};
</script>

<style scoped>
.alert {
  margin-top: 1rem;
}
.spinner-border {
  vertical-align: middle;
}
.alert-warning {
  background-color: #fff3cd;
  border-color: #ffecb5;
  color: #664d03;
}
</style>
